// src/pages/ReportsPage.js
// รายงานสรุปเงินเดือน + คอมมิชชั่น ต่อพนักงาน ตามช่วงวันที่
// ใช้ข้อมูลจาก /payroll/summary ตาม routes.py ปัจจุบัน
// ปรับ UI ให้เข้าธีมโมเดิร์นขาว-เทา-ชมพูอ่อน

import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { useNotification } from "../NotificationContext";
import { Bar } from "react-chartjs-2";
import SkeletonLoader from "../components/SkeletonLoader";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ReportsPage = () => {
  const { addNotification } = useNotification();

  // Helpers
  const getDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getMonthRange = (m, y) => {
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { start: getDateString(start), end: getDateString(end) };
  };

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const initialRange = getMonthRange(currentMonth, currentYear);

  const [employees, setEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState("all");

  // Default to current month
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);

  // Quick Filter State
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // โหลดพนักงานสำหรับ filter
  const loadEmployees = async () => {
    try {
      const res = await api.get("/employees");
      setEmployees(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadReport(); // Auto load current month
  }, []);

  const formatMoney = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ดึงรายงานจาก backend (/payroll/summary)
  const loadReport = async (startOverride, endOverride) => {
    try {
      setLoading(true);

      const sDate = startOverride !== undefined ? startOverride : startDate;
      const eDate = endOverride !== undefined ? endOverride : endDate;

      // ✅ เช็คช่วงวันที่ ไม่ให้เกิน 1 ปี
      if (sDate || eDate) {
        const start = sDate ? new Date(sDate) : null;
        const end = eDate ? new Date(eDate) : new Date();

        if (start && end) {
          const diffMs = end.getTime() - start.getTime();
          const diffDays = diffMs / (1000 * 60 * 60 * 24);

          if (diffDays > 365) {
            setRows([]);
            setLoading(false);
            addNotification(
              "ช่วงวันที่ค้นหาได้ไม่เกิน 1 ปี กรุณาปรับวันที่ใหม่",
              "warning"
            );
            return;
          }
        }
      }

      const params = new URLSearchParams();
      if (sDate) params.append("date_from", sDate);
      if (eDate) params.append("date_to", eDate);

      const res = await api.get(`/payroll/summary?${params.toString()}`);
      setRows(res.data || []);
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดข้อมูลรายงานไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  // กด Enter ในช่องวันที่ให้ยิงโหลดรายงาน
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      loadReport();
    }
  };

  // Export CSV จาก rows ปัจจุบัน
  const handleExportCsv = () => {
    if (!rows || rows.length === 0) return;

    const header = [
      "emp_id",
      "emp_name",
      "base_salary",
      "total_commission",
      "total_pay",
    ];

    const csvRows = [
      header.join(","),
      ...rows.map((r) =>
        [
          r.emp_id,
          `"${(r.emp_name || "").replace(/"/g, '""')}"`,
          r.base_salary || 0,
          r.total_commission || 0,
          r.total_pay || 0,
        ].join(",")
      ),
    ];

    const csvContent = "\uFEFF" + csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const fileName = `report_payroll_${startDate || "all"}_${endDate || "all"
      }.csv`;

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // filter ตามพนักงาน (ด้านหน้า)
  const filteredRows = useMemo(() => {
    if (selectedEmpId === "all" || !selectedEmpId) return rows;
    return rows.filter((r) => String(r.emp_id) === String(selectedEmpId));
  }, [rows, selectedEmpId]);

  // รวมยอดทุกคน
  const totalSummary = useMemo(
    () =>
      filteredRows.reduce(
        (acc, r) => {
          acc.base_salary += Number(r.base_salary) || 0;
          acc.total_commission += Number(r.total_commission) || 0;
          acc.total_deduction += Number(r.total_deduction) || 0;
          acc.total_pay += Number(r.total_pay) || 0;
          return acc;
        },
        { base_salary: 0, total_commission: 0, total_deduction: 0, total_pay: 0 }
      ),
    [filteredRows]
  );

  // ข้อมูลกราฟแท่ง: พนักงานเป็นแกนหลัก (เรียงจากรายได้รวมมากไปน้อย) และแสดงแบบ Stacked (เงินเดือน+คอม)
  const chartData = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) {
      return { labels: [], datasets: [] };
    }
    const sorted = [...filteredRows].sort((a, b) => (Number(b.total_pay || 0) - Number(a.total_pay || 0)));
    return {
      labels: sorted.map((r) => r.emp_name || `Emp ${r.emp_id}`),
      datasets: [
        {
          label: "เงินเดือนพื้นฐาน (บาท)",
          data: sorted.map((r) => Number(r.base_salary) || 0),
          backgroundColor: "#8B7355",
          stack: 'total'
        },
        {
          label: "คอมมิชชั่นรวม (บาท)",
          data: sorted.map((r) => Number(r.total_commission) || 0),
          backgroundColor: "#D4AF37",
          stack: 'total'
        },
      ],
    };
  }, [filteredRows]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // horizontal bars to fit all employees visibly
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: function (ctx) {
            const val = ctx.raw || 0;
            return `${ctx.dataset.label}: ${val.toLocaleString("th-TH", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ฿`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { callback: function (value) { return value.toLocaleString("th-TH"); } },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      y: { grid: { display: false } },
    },
    interaction: { intersect: false, mode: 'nearest' },
    datasets: { bar: { barThickness: 14 } },
    scalesStacked: true
  }), []);

  const handleMonthChange = (e) => {
    const m = parseInt(e.target.value);
    setSelectedMonth(m);
    const { start, end } = getMonthRange(m, selectedYear);
    setStartDate(start);
    setEndDate(end);
    loadReport(start, end);
  };

  const handleYearChange = (e) => {
    const y = parseInt(e.target.value);
    setSelectedYear(y);
    const { start, end } = getMonthRange(selectedMonth, y);
    setStartDate(start);
    setEndDate(end);
    loadReport(start, end);
  };

  const months = [
    { id: 1, name: "มกราคม" }, { id: 2, name: "กุมภาพันธ์" }, { id: 3, name: "มีนาคม" },
    { id: 4, name: "เมษายน" }, { id: 5, name: "พฤษภาคม" }, { id: 6, name: "มิถุนายน" },
    { id: 7, name: "กรกฎาคม" }, { id: 8, name: "สิงหาคม" }, { id: 9, name: "กันยายน" },
    { id: 10, name: "ตุลาคม" }, { id: 11, name: "พฤศจิกายน" }, { id: 12, name: "ธันวาคม" }
  ];

  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="dashboard-page reports-page container">
      {/* Hero */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-file-earmark-bar-graph me-2"></i>รายงานเงินเดือน & คอมมิชชั่น
          </div>
          <div className="dashboard-hero-subtitle">
            สรุปเงินเดือนพื้นฐาน + คอมมิชชั่นตามช่วงวันที่ ต่อพนักงานแต่ละคน
          </div>
        </div>
      </div>

      {/* ฟิลเตอร์ */}
      <div className="card card-ks-soft mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0 fw-bold text-secondary">
              <i className="bi bi-funnel me-2"></i>ตัวกรองรายงาน
            </h5>
            {loading && (
              <span className="text-muted small">กำลังโหลดข้อมูล...</span>
            )}
          </div>

          {/* Quick Filter: Month/Year */}
          <div className="row g-2 align-items-center mb-3 pb-3 border-bottom">
            <div className="col-auto">
              <span className="fw-semibold small text-muted"><i className="bi bi-calendar-month me-1"></i>เลือกเดือน/ปี:</span>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={selectedMonth} onChange={handleMonthChange} style={{ width: 120 }}>
                {months.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={selectedYear} onChange={handleYearChange} style={{ width: 100 }}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label className="form-label form-label-sm">พนักงาน</label>
              <select
                className="form-select form-select-sm"
                value={selectedEmpId}
                onChange={(e) => setSelectedEmpId(e.target.value)}
              >
                <option value="all">ทุกคน</option>
                {employees.map((e) => (
                  <option key={e.emp_id} value={e.emp_id}>
                    {e.emp_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 col-sm-6">
              <label className="form-label form-label-sm">วันที่เริ่มต้น</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </div>

            <div className="col-md-3 col-sm-6">
              <label className="form-label form-label-sm">วันที่สิ้นสุด</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </div>

            <div className="col-md-3 col-sm-6 d-flex flex-column gap-2">
              <button
                type="button"
                className="btn btn-ks-success btn-sm w-100"
                onClick={loadReport}
                disabled={loading}
              >
                <i className="fas fa-refresh me-1" style={{ display: loading ? "inline" : "none" }}></i>
                {loading ? "กำลังโหลด..." : "โหลดรายงาน"}
              </button>
              <button
                type="button"
                className="btn btn-ks-outline btn-sm w-100"
                onClick={handleExportCsv}
                disabled={rows.length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="mt-2 small text-muted">
            * ช่วงวันที่ค้นหาได้ไม่เกิน 1 ปี หากไม่เลือกวันที่ ระบบจะแสดงข้อมูลทั้งหมดที่มี
          </div>
        </div>
      </div>

      {/* กราฟสรุป (ซ้าย) + สรุปตัวเลข (ขวา) */}
      <div className="card card-ks-soft mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-stretch">
            <div className="col-lg-8 col-md-7">
              <h5 className="card-title mb-2 fw-bold text-secondary">
                <i className="bi bi-bar-chart me-2"></i>รายได้รวมต่อพนักงาน (เงินเดือน + คอมมิชชั่น)
              </h5>
              {loading ? (
                <SkeletonLoader type="card" />
              ) : filteredRows.length === 0 ? (
                <div className="text-muted small">ยังไม่มีข้อมูลสำหรับแสดงกราฟ กรุณากด "โหลดรายงาน" ก่อน</div>
              ) : (
                <div style={{ height: Math.min(360, Math.max(180, 24 + filteredRows.length * 22)) }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              )}
            </div>
            <div className="col-lg-4 col-md-5">
              <h6 className="mb-2 fw-bold text-secondary">
                <i className="bi bi-calculator me-2"></i>สรุปภาพรวม
              </h6>
              <div className="bg-light border rounded p-3" style={{ fontFamily: 'var(--font-thai)' }}>
                <div className="d-flex justify-content-between align-items-center mb-2"><span className="text-muted">จำนวนพนักงาน</span><span className="fw-bold fs-5">{filteredRows.length.toLocaleString()} คน</span></div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between align-items-center mb-1"><span className="text-secondary">รวมเงินเดือนพื้นฐาน</span><span className="fw-bold">{formatMoney(totalSummary.base_salary)} ฿</span></div>
                <div className="d-flex justify-content-between align-items-center mb-1"><span className="text-secondary">รวมคอมมิชชั่น</span><span className="fw-bold">{formatMoney(totalSummary.total_commission)} ฿</span></div>
                <div className="d-flex justify-content-between align-items-center mb-1"><span className="text-secondary">รวมยอดหัก</span><span className="fw-bold text-danger">{formatMoney(totalSummary.total_deduction)} ฿</span></div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between align-items-center"><span className="fw-bold" style={{ color: "#d4af37" }}>รายได้รวมสุทธิ</span><span className="fw-bold fs-5" style={{ color: "#d4af37" }}>{formatMoney(totalSummary.total_pay)} ฿</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ตารางรายละเอียด */}
      <div className="card card-ks-soft mb-3">
        <div className="card-body">
          <h5 className="card-title mb-3 fw-bold text-secondary">
            <i className="bi bi-table me-2"></i>รายละเอียดสรุปต่อพนักงาน
          </h5>

          {loading ? (
            <SkeletonLoader type="table" rows={8} columns={5} />
          ) : filteredRows.length === 0 ? (
            <div className="text-muted">
              ยังไม่มีข้อมูล กรุณากด "โหลดรายงาน"
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th>พนักงาน</th>
                    <th className="text-end">เงินเดือนพื้นฐาน</th>
                    <th className="text-end">คอมมิชชั่นรวม</th>
                    <th className="text-end">ยอดหัก</th>
                    <th className="text-end">รายได้รวม (สุทธิ)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.emp_id}>
                      <td>{r.emp_name}</td>
                      <td className="text-end">
                        {formatMoney(r.base_salary)} ฿
                      </td>
                      <td className="text-end">
                        {formatMoney(r.total_commission)} ฿
                      </td>
                      <td className="text-end text-danger">{formatMoney(r.total_deduction || 0)} ฿</td>
                      <td className="text-end">{formatMoney(r.total_pay)} ฿</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td>รวมทั้งหมด</td>
                    <td className="text-end">
                      {formatMoney(totalSummary.base_salary)} ฿
                    </td>
                    <td className="text-end">
                      {formatMoney(totalSummary.total_commission)} ฿
                    </td>
                    <td className="text-end text-danger">{formatMoney(totalSummary.total_deduction)} ฿</td>
                    <td className="text-end">{formatMoney(totalSummary.total_pay)} ฿</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
